import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ActionType } from 'typesafe-actions'
import type { TPortfolio } from '../portfolio/types'
import { NAMESPACE } from './constants'
import {
  TCombinedSearchResponse,
  TGooglePlace,
  TLightstoneProperty,
  TProperty,
  TPropertyCreateRequest,
  TPropertySearchResult,
  TPropertyState,
  TPropertySummary,
  TPropertyUpdateRequest,
  TSearchRequest,
} from './types'

const initialState: TPropertyState = {
  googlePlaces: {}, // fetched google place entities
  properties: {}, // fetched property entities
  lightstoneProperties: {}, // fetched lightstone property entities
  summaries: {}, // GET properties response
  searchResults: {
    entities: {},
    byQuery: {},
  },
}

/**
 * Selectors live in `./selectors` and the pure address helper in `./utils`; this module is the
 * slice only.
 *
 * It must not import `api/portfolio` at runtime: `api/portfolio/state` imports this module for
 * `propertyPortfolios_success`, so the reverse import would be a require cycle. The one portfolio
 * type below is an `import type`, which is erased.
 */
const propertyApiSlice = createSlice({
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    createProperty_request: (state, { payload }: PayloadAction<TPropertyCreateRequest>) => {},
    createProperty_success: (state, { payload }: PayloadAction<TProperty>) => {
      state.properties[payload.id] = payload
    },
    updateProperty_request: (state, { payload }: PayloadAction<TPropertyUpdateRequest>) => {},
    updateProperty_success: (state, { payload }: PayloadAction<TProperty>) => {
      state.properties[payload.id] = payload
    },
    properties_request: () => {},
    properties_success: (state, { payload }: PayloadAction<TPropertySummary[]>) => {
      payload.forEach(p => {
        state.summaries[p.propertyId] = p
      })
    },
    search_request: (state, { payload }: PayloadAction<TSearchRequest>) => {},
    search_success: (
      state,
      { payload: { response, query } }: PayloadAction<{ response: TPropertySearchResult[]; query: string }>,
    ) => {
      state.searchResults.entities[query] = {
        properties: response,
      }
      state.searchResults.byQuery[query] = {
        results: response.map(({ id, placeId, lightstoneId }) => {
          if (id) {
            return { id }
          }
          if (placeId) {
            return { placeId }
          }
          if (lightstoneId) {
            return { lightstoneId }
          }
          return undefined
        }),
      }
    },
    combinedSearch_request: (state, { payload }: PayloadAction<TSearchRequest>) => {},
    combinedSearch_success: (
      state,
      { payload: { response, query } }: PayloadAction<{ response: TCombinedSearchResponse; query: string }>,
    ) => {
      const { googleProperties } = response
      state.searchResults.entities[query] = {
        places: googleProperties,
      }
      state.searchResults.byQuery[query] = {
        places: googleProperties.map(p => p.placeId),
      }
    },
    place_request: (state, { payload }: PayloadAction<{ placeId: string }>) => {},
    place_success: (
      state,
      { payload: { placeId, response } }: PayloadAction<{ placeId: string; response: TGooglePlace }>,
    ) => {
      state.googlePlaces[placeId] = response
    },
    property_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    property_success: (state, { payload }: PayloadAction<TProperty>) => {
      state.properties[payload.id] = payload
    },
    lightstoneProperty_request: (state, { payload }: PayloadAction<{ lightstoneId: string }>) => {},
    lightstoneProperty_success: (state, { payload }: PayloadAction<TLightstoneProperty>) => {
      state.lightstoneProperties[payload.lightstoneId] = payload
    },
    propertyPortfolios_request: (state, { payload }: PayloadAction<{ id: string }>) => {},
    /**
     * Consumed by `api/portfolio`, which stores the portfolios in its own `byId`. This slice used
     * to also keep a `portfolios` index (property id → portfolio ids), maintained here and by a
     * hard-coded `'portfolioApi/amendProperty_success'` case — but nothing ever read it, so both
     * went, and with them the cross-module string key.
     */
    propertyPortfolios_success: (
      state,
      { payload }: PayloadAction<{ response: TPortfolio[]; propertyId: string }>,
    ) => {},
  },
})

const { reducer, actions: events } = propertyApiSlice

export type PropertyEvent = ActionType<typeof events>

export { events, initialState, reducer }
